import * as React from "react";

import CardMembershipIcon from "@mui/icons-material/CardMembership";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { getAuth } from "firebase/auth";

import { addWebbySession } from "../../util/addWebbySession";
import fetchApi from "../../util/fetchApi";
import { navigateTo } from "../../util/navigate";

export default function ClubInClubMenuItem({
    url,
    onClose,
}: {
    url: string;
    onClose: () => void;
}): React.JSX.Element {
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const request = React.useRef<AbortController | null>(null);
    const active = React.useRef(false);

    React.useEffect(() => {
        active.current = true;
        return () => {
            active.current = false;
            request.current?.abort();
        };
    }, []);

    const openClub = async () => {
        // This is deliberately click-only; rendering or opening the menu mints nothing.
        if (request.current) return;
        const user = getAuth().currentUser;
        if (!user) {
            setError(
                "Please sign in to Volley before opening your membership.",
            );
            return;
        }
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 10_000);
        request.current = controller;
        setBusy(true);
        setError(null);
        try {
            const result = await fetchApi<{ token: string }>(
                "/api/users/token",
                "GET",
                undefined,
                { signal: controller.signal, cache: "no-store" },
            );
            if (!active.current || controller.signal.aborted) return;
            if (getAuth().currentUser !== user) {
                setError("Your sign-in changed. Please try again.");
                return;
            }
            if (typeof result.token !== "string" || !result.token.trim()) {
                throw new Error("Missing handoff token");
            }
            const destination = new URL(url);
            destination.searchParams.set("token", result.token);
            addWebbySession(destination);
            navigateTo(destination.href);
            onClose();
        } catch {
            if (active.current) {
                setError("Unable to open Volley membership. Please try again.");
            }
        } finally {
            window.clearTimeout(timeout);
            request.current = null;
            if (active.current) setBusy(false);
        }
    };

    return (
        <>
            <ListItemButton
                disabled={busy}
                onClick={(event) => {
                    // Keep the temporary drawer open while requesting, including errors.
                    event.stopPropagation();
                    void openClub();
                }}
            >
                <ListItemIcon>
                    {busy ? (
                        <CircularProgress size={24} sx={{ color: "white" }} />
                    ) : (
                        <CardMembershipIcon sx={{ color: "white" }} />
                    )}
                </ListItemIcon>
                <ListItemText
                    primary={
                        busy
                            ? "Opening Volley membership…"
                            : "Volley membership"
                    }
                    sx={{ color: "white" }}
                />
            </ListItemButton>
            {error && <Alert severity="error">{error}</Alert>}
        </>
    );
}
